import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  TablePagination,
  Pagination,
  Button,
  MenuItem,
  Divider,
  Card,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useTableState } from '../../../../hooks/useTableState';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from '../../../../commons/utils';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import { IconByString } from '../../../../components/IconByString';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import {
  getAreasCalificacionesStore,
  getAreasRatingsBySearch,
  storeDeleteAreasRating,
  storeRestoreAreasRating,
  getAreasRatingsByPagination,
  getAreaRatingDetailStore,
} from '../../../../store/ratings/thunks';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { FormAreaModal } from './FormAreaModal';
import { SinDatos } from '../../../../commons/generics/SinDatos';

export const TableAreas = () => {
  const dispatch = useDispatch();
  const { areas_ratings_list, area_rating } = useSelector((state: any) => state.ratingsDevices);

  // TABLE STATE HOOK
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    anchorEl,
    setAnchorEl,
    openElem,
    setOpenElem,
    handleOpenMenu,
    handleCloseMenu,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    updatePaginationFromList,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    ResponseComponents,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('qiratingsareas');

  // Local State
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [openModalForm, setOpenModalForm] = useState(false);
  const [selectedArea, setSelectedArea] = useState<any>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    updatePaginationFromList(areas_ratings_list);
  }, [areas_ratings_list]);

  useEffect(() => {
    setSelectedArea(area_rating);
  }, [area_rating]);

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);

    const response = await dispatch(getAreasRatingsByPagination(value, checked ? 0 : 1) as any);
    await handleResponse(response, 'listar áreas', false);
  };

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getAreasRatingsBySearch(p, checked ? 0 : 1),
      getInitialData,
      null,
      'buscar áreas'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getAreasRatingsBySearch(p, status),
        () => getInitialData(status),
        null,
        'buscar áreas'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(getInitialData);
  };

  useEffect(() => {
    checkPermissions();
    getInitialData();
  }, []);

  useEffect(() => {
    if (areas_ratings_list) {
      updatePaginationFromList(areas_ratings_list);
    }
  }, [areas_ratings_list]);

  const getInitialData = async (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    const response = await dispatch(getAreasCalificacionesStore(status));
    await handleResponse(response, 'obtener áreas', false);
    if (response && (response.ok || response.code === 200)) {
      updatePaginationFromList(response.data || []);
    }
  };

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.configuracion', 'modulo.areas', 'view');
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions('modulo.configuracion', 'modulo.areas', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.configuracion', 'modulo.areas', 'delete');
    if (del) setCanDelete(true);

    const rest = await hasPemissions('modulo.configuracion', 'modulo.areas', 'restore');
    if (rest) setCanRestore(true);
  };

  const handleView = async (area: any) => {
    const response = await dispatch(getAreaRatingDetailStore(area.id));
    await handleResponse(response, 'obtener detalle', false);
    if (response?.ok) {
      setIsViewMode(true);
      setOpenModalForm(true);
    }
  };

  const handleEdit = async (area: any) => {
    const response = await dispatch(getAreaRatingDetailStore(area.id));
    await handleResponse(response, 'obtener detalle', false);
    if (response?.ok) {
      setIsViewMode(false);
      setOpenModalForm(true);
    }
  };

  const handleDelete = (area: any) => {
    setOpenElem(area);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (area: any) => {
    setOpenElem(area);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDeleteAreasRating(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar área', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeRestoreAreasRating(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar área', true);
  };

  const handleCloseForm = () => {
    setOpenModalForm(false);
    setSelectedArea(null);
    setIsViewMode(false);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'description'],
    'status',
    1
  );

  const headCells: HeadCell[] = [
    { id: 'name', label: 'Area', sortable: true },
    { id: 'description', label: 'Descripción', sortable: true },
    { id: 'status', label: 'Estado', sortable: true },
    { id: 'created_by', label: 'Creado Por', sortable: true },
    { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
  ];

  return (
    <Box sx={{ width: '100%', mt: 5 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar dispositivo..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas (Areas)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <SinDatos />
                  </StyledTableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any) => (
                    <StyledTableRow key={row.id} hover>
                      <StyledTableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <IconByString
                            iconName="DashboardCustomize"
                            sx={{ color: 'primary.main', fontSize: 20 }}
                          />
                          <ClampedText
                            text={usarFuncionesComposicion(
                              corregirTexto,
                              firstLetterUpperCase,
                              row.name
                            )}
                          />
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.description
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.createdBy?.name || ''
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && handleView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestore(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
          {dataTableList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataTableList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
            />
          )}

          {dataTableList.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                padding: 2.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Pagination
                sx={{ justifyContent: 'center' }}
                color="primary"
                count={itemCountPagination}
                page={pagePagination}
                onChange={changePaginationPage}
                showFirstButton
                showLastButton
                shape="circular"
              />
            </Box>
          )}
        </TableContainer>
      </Card>

      <FormAreaModal
        open={openModalForm}
        handleClose={handleCloseForm}
        refreshData={() => getInitialData()}
        areaData={selectedArea}
        isViewMode={isViewMode}
      />

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
